import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const iconSizeClasses = {
  xs: "text-[12px]",
  "xs-sm": "text-[14px]",
  sm: "text-[16px]",
  md: "text-[18px]",
  lg: "text-[20px]",
  xl: "text-[24px]",
};

const iconColorClasses = {
  primary: "text-primary",
  warning: "text-warning",
  success: "text-success",
  danger: "text-danger",
  black: "text-black",
  white: "text-gray-400",
};
// Update tooltipVariants for outlined variant
const tooltipVariants = {
  contained: {
    primary: "bg-primary text-white shadow-lg border-2 border-primary-900",
    warning: "bg-warning-500 text-white shadow-lg border-2 border-warning-900",
    success: "bg-success-500 text-white shadow-lg border-2 border-success-900",
    danger: "bg-danger-500 text-white shadow-lg border-2 border-danger-900",
    black: "bg-black text-white border-2 border-gray-300",
    white: "bg-white text-black shadow-lg border-2 border-gray-900",
  },
  outlined: {
    primary: "bg-white text-primary border-2 border-primary-900",
    warning: "bg-white text-warning-500 border-2 border-warning-900",
    success: "bg-white text-success-500 border-2 border-success-900",
    danger: "bg-white text-danger-500 border-2 border-danger-900",
    black: "bg-white text-black border-2 border-black",
    white: "bg-white text-gray-800 border-2 border-gray-900",
  },
  text: {
    primary: "bg-primary-50 text-primary",
    warning: "bg-warning-50 text-warning",
    success: "bg-success-50 text-success",
    danger: "bg-danger-50 text-danger",
    black: "bg-gray-100 text-black",
    white: "bg-gray-900 text-white",
  },
};
// Update arrowStyles with background color matching
// Move arrowVariants before arrowStyles
// Update arrowVariants to match background colors
const arrowVariants = {
  contained: {
    primary:
      "border-primary-900 after:border-primary before:border-primary-900",
    warning:
      "border-warning-900 after:border-warning-500 before:border-warning-900",
    success:
      "border-success-900 after:border-success-500 before:border-success-900",
    danger:
      "border-danger-900 after:border-danger-500 before:border-danger-900",
    black: "border-gray-300 after:border-black before:border-gray-300",
    white: "border-gray-200 after:border-white before:border-gray-200",
  },
  outlined: {
    primary: "border-primary after:border-white before:border-primary",
    warning: "border-warning-500 after:border-white before:border-warning-500",
    success: "border-success-500 after:border-white before:border-success-500",
    danger: "border-danger-500 after:border-white before:border-danger-500",
    black: "border-black after:border-white before:border-black",
    white: "border-gray-300 after:border-white before:border-gray-300",
  },
  text: {
    primary: "border-primary-50 after:border-primary-50",
    warning: "border-warning-50 after:border-warning-50",
    success: "border-success-50 after:border-success-50",
    danger: "border-danger-50 after:border-danger-50",
    black: "border-gray-100 after:border-gray-100",
    white: "border-gray-900 after:border-gray-900",
  },
};

// Then arrowStyles follows
// Update arrowStyles with adjusted border widths
// Update arrow position for paragraphs
const arrowStyles = {
  top: {
    position: "bottom-[-11px] left-1/2 -translate-x-1/2",
    border: "border-t-[11px] border-x-[11px] border-b-0 border-x-transparent",
    after:
      "after:content-[''] after:absolute after:border-t-[9px] after:border-x-[9px] after:border-b-0 after:border-x-transparent after:top-[-11px] after:left-[-9px]",
  },
  bottom: {
    position: "top-[-11px] left-1/2 -translate-x-1/2",
    border: "border-b-[11px] border-x-[11px] border-t-0 border-x-transparent",
    after:
      "after:content-[''] after:absolute after:border-b-[9px] after:border-x-[9px] after:border-t-0 after:border-x-transparent after:top-[2px] after:left-[-9px]",
  },
  left: {
    position: "right-[-11px] top-1/2 -translate-y-1/2",
    border: "border-l-[11px] border-y-[11px] border-r-0 border-y-transparent",
    after:
      "after:content-[''] after:absolute after:border-l-[9px] after:border-y-[9px] after:border-r-0 after:border-y-transparent after:left-[-11px] after:top-[-9px]",
  },
  right: {
    position: "left-[-11px] top-1/2 -translate-y-1/2",
    border: "border-r-[11px] border-y-[11px] border-l-0 border-y-transparent",
    after:
      "after:content-[''] after:absolute after:border-r-[9px] after:border-y-[9px] after:border-l-0 after:border-y-transparent after:left-[2px] after:top-[-9px]",
  },
};
// Update the arrow div in the render section
// First, remove this standalone JSX element that's causing the error
// <div
//   className={`absolute border-solid before:content-['']
//     ${arrowStyles[adjustedPosition].position}
//     ${arrowStyles[adjustedPosition].border}
//     ${arrowStyles[adjustedPosition].after}
//     ${arrowVariants[variant][color]}`}
// />;
// Keep all the constant definitions (tooltipVariants, arrowStyles, arrowVariants)

const ToolTip = ({
  children,
  description,
  size = "md", // xs, xs-sm, sm, md, lg, xl
  color = "black", // primary, warning, success, danger, black, white
  tooltipPosition = "right", // top, bottom, left, right
  variant = "contained", // outlined, text
  delay = 0,
  offset = 18,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(tooltipPosition);
  const [tooltipCoords, setTooltipCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setShowTooltip(true), delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowTooltip(false);
  };
  const updatePosition = () => {
    if (showTooltip && tooltipRef.current && containerRef.current) {
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const container = containerRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      // Calculate available space in each direction
      const space = {
        top: container.top,
        bottom: viewport.height - container.bottom,
        left: container.left,
        right: viewport.width - container.right,
      };

      // Find the direction with most space
      let bestPosition = tooltipPosition;
      let maxSpace = 0;

      Object.entries(space).forEach(([position, available]) => {
        if (available > maxSpace) {
          maxSpace = available;
          bestPosition = position;
        }
      });

      let x = 0;
      let y = 0;

      // Calculate base position using the best available space
      switch (bestPosition) {
        case "top":
          x = container.left + container.width / 2 - tooltip.width / 2;
          y = container.top - tooltip.height - offset;
          break;
        case "bottom":
          x = container.left + container.width / 2 - tooltip.width / 2;
          y = container.bottom + offset;
          break;
        case "left":
          x = container.left - tooltip.width - offset;
          y = container.top + (container.height - tooltip.height) / 2;
          break;
        case "right":
          x = container.right + offset;
          y = container.top + (container.height - tooltip.height) / 2;
          break;
        default:
          break;
      }

      // Adjust horizontal position if tooltip goes beyond viewport
      if (x < 0) {
        x = offset;
      } else if (x + tooltip.width > viewport.width) {
        x = viewport.width - tooltip.width - offset;
      }

      // Adjust vertical position if tooltip goes beyond viewport
      if (y < 0) {
        y = offset;
      } else if (y + tooltip.height > viewport.height) {
        y = viewport.height - tooltip.height - offset;
      }

      setTooltipCoords({ x, y });
      setAdjustedPosition(bestPosition);
    }
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [showTooltip, tooltipPosition]);

  return (
    <div className="inline-flex items-center">
      {children}
      <div className="inline-flex items-center relative" ref={containerRef}>
        <span
          className={`material-symbols-rounded cursor-pointer font-light ${iconSizeClasses[size]} ${iconColorClasses[color]}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          info
        </span>
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              ref={tooltipRef}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              style={{
                position: "fixed",
                left: tooltipCoords.x,
                top: tooltipCoords.y,
                zIndex: 50,
              }}
              className={`px-4 py-3 text-xs rounded-md whitespace-normal break-words max-w-[300px]
                ${tooltipVariants[variant][color]}`}
            >
              {typeof description === "string" ? (
                description
              ) : (
                <div className="tooltip-content">{description}</div>
              )}
              <div
                className={`absolute border-solid before:content-[''] before:absolute before:z-[-1]
                  ${arrowStyles[adjustedPosition].position}
                  ${arrowStyles[adjustedPosition].border}
                  ${arrowStyles[adjustedPosition].after}
                  ${arrowVariants[variant][color]}`}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ToolTip;
